/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Loader2, ShieldAlert, Bug, ShieldCheck } from 'lucide-react';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { purgeQuarantine, PURGE_CONFIRM_WORD } from '@/lib/antiFraud';

/**
 * PurgeQuarantineDialog — P1: eliminazione in blocco degli account in quarantena.
 * Doppia sicurezza: scelta esplicita degli stati + digitazione della parola di conferma.
 * Ogni eliminazione è tracciata nell'Audit Log SHA-256 lato server.
 */
export const PurgeQuarantineDialog = ({ counts = {}, onDone }) => {
  const [open, setOpen] = useState(false);
  const [statuses, setStatuses] = useState({ TEST_ACCOUNT: true, SUSPECTED: false });
  const [confirm, setConfirm] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const selected = Object.entries(statuses).filter(([, v]) => v).map(([k]) => k);
  const ready = selected.length > 0 && confirm.trim().toUpperCase() === PURGE_CONFIRM_WORD && !busy;

  useEffect(() => {
    if (!open) return;
    if (selected.length === 0) { setPreview({ count: 0 }); return; }
    let alive = true;
    purgeQuarantine({ confirm: PURGE_CONFIRM_WORD, statuses: selected, dryRun: true }).then((r) => { if (alive) setPreview(r); }).catch(() => { if (alive) setPreview(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, statuses.TEST_ACCOUNT, statuses.SUSPECTED]);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!ready) return;
    setBusy(true);
    try {
      const r = await purgeQuarantine({ confirm: confirm.trim(), statuses: selected });
      toast.success(`Eliminati ${r.deleted} account in quarantena (tracciato nell'audit SHA-256)`);
      setOpen(false); setConfirm('');
      onDone?.(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Eliminazione non riuscita');
    } finally { setBusy(false); }
  };

  const total = (statuses.TEST_ACCOUNT ? counts.TEST_ACCOUNT || 0 : 0) + (statuses.SUSPECTED ? counts.SUSPECTED || 0 : 0);

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirm(''); }}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="h-10 rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid="validation-purge-btn">
          <Trash2 className="h-4 w-4" /> Elimina in blocco
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-xl" data-testid="validation-purge-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-destructive" /> Eliminare gli account in quarantena?</AlertDialogTitle>
          <AlertDialogDescription>
            L'operazione è <b>irreversibile</b>: account e dati collegati (post, notifiche, chiavi portale, note) vengono rimossi. Gli amministratori non vengono mai eliminati. Ogni eliminazione genera un record immutabile nell'Audit Log SHA-256.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={run} className="space-y-4">
          <div className="space-y-2">
            <label className="flex items-center gap-3 rounded-xl border border-border/70 p-3 cursor-pointer">
              <Checkbox checked={statuses.TEST_ACCOUNT} onCheckedChange={(v) => setStatuses((s) => ({ ...s, TEST_ACCOUNT: !!v }))} data-testid="validation-purge-test" />
              <Bug className="h-4 w-4 text-destructive" />
              <span className="text-sm flex-1">Account di test <span className="text-muted-foreground">(bot, email temporanee, pattern di script)</span></span>
              <span className="text-xs font-mono">{counts.TEST_ACCOUNT ?? 0}</span>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-border/70 p-3 cursor-pointer">
              <Checkbox checked={statuses.SUSPECTED} onCheckedChange={(v) => setStatuses((s) => ({ ...s, SUSPECTED: !!v }))} data-testid="validation-purge-suspected" />
              <ShieldAlert className="h-4 w-4 text-warning" />
              <span className="text-sm flex-1">Sospetti <span className="text-muted-foreground">(profili con anomalie: valuta prima caso per caso)</span></span>
              <span className="text-xs font-mono">{counts.SUSPECTED ?? 0}</span>
            </label>
          </div>
          <div className="rounded-xl bg-muted/40 border border-border/70 p-3 text-sm flex items-center gap-2" data-testid="validation-purge-preview">
            <ShieldCheck className="h-4 w-4 text-primary" /> Verranno eliminati <b>{preview?.count ?? total}</b> account.
          </div>
          <div>
            <Label htmlFor="purge-confirm">Digita <span className="font-mono font-bold">{PURGE_CONFIRM_WORD}</span> per confermare</Label>
            <Input id="purge-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" className="h-11 rounded-xl font-mono uppercase mt-1" data-testid="validation-purge-confirm-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="validation-purge-cancel">Annulla</AlertDialogCancel>
            <Button type="submit" variant="destructive" disabled={!ready} className="rounded-xl" data-testid="validation-purge-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Elimina definitivamente
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PurgeQuarantineDialog;
